import { Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import PageFallback from './components/PageFallback'
import useDefinedSentryUser from './hooks/useDefinedSentryUser'
import useFcmToken from '@/modules/Notification/hooks/useFcmToken'
import AuthLayout from './layouts/AuthLayout'
import RootLayout from './layouts/RootLayout'
import APIUsageTemplateModule from './modules/APIUsageTemplate'
import AuthModule from './modules/Auth'
import { BusinessRoutes } from './app/register-business'
import ContractModule from './modules/Contract'
import CustomerModule from './modules/Customer'
import DashboardModule from './modules/Dashboard'
import { DayoffsRoutes } from './app/register-dayoffs'
import { OncallAdminModule } from './modules/Oncall'
import DocumentModule from './modules/Documents'
import EmployeeModule, { EmployeePublicModule } from './modules/Employee'
import HolidayModule from './modules/Holiday'
import InvoiceModule from './modules/Invoice'
import JobCategoryModule from './modules/JobCategory'
import JobModule from './modules/Jobs'
import KPIModule from './modules/KPI'
import MarketingModule from './modules/Marketing'
import NotFoundModule from './modules/NotFound'
import ReleaseNotesModule from './modules/ReleaseNotes'
import ReportModule from './modules/Report'

import ResumeModule from './modules/Resume'
import RolesModule from './modules/Roles'
import ServiceModule from './modules/Service'
import TicketModule, {
    TicketFeedbackPublic,
    TicketPublicModule,
} from './modules/Ticket'
import { VCard, VCardPublic } from './modules/VCard'
import { AccountWorkDayModule, WorkDayModule } from './modules/WorkDay'
import AccountModule from './modules/Account'
import { DriveRoute } from './app/register-drive'
import { SettingsRoute } from './app/register-settings'
import { TendersRoute } from './app/register-tenders'
import { SurveyRoute } from './app/register-survey'
import MailerCampaignModule from './modules/MailerCampaign'
import { WarehouseModule } from './app/register-warehouse'
import ErrorPage from './components/ErrorBoundary/ErrorPage'
import { LivechatRoute } from './app/register-livechat'
import { ResellerRoute } from './app/register-reseller'
import AffiliateModule from './modules/Affiliate'
import PromoModule from './modules/Promo'
import TechnicalModule from './modules/Technical'
import TechnicalReportModule from './modules/Technical/Report'
import WatchpointModule from './modules/Technical/Watchpoint'
import ThresholdModule from './modules/Technical/Threshold'
import AssetModule from './modules/Asset'
import ToolsModule from './modules/Tools'
import PurchasingPaymentsModule from './modules/PurchasingPayments'
import OperationsModule from './modules/Operations'

// Router created at module level to prevent recreation on HMR updates
const router = createBrowserRouter([
    {
        path: '/',
        element: <RootLayout />,
        children: [
            DashboardModule,
            NotFoundModule,
            EmployeeModule,
            ...BusinessRoutes,
            CustomerModule,
            HolidayModule,
            WorkDayModule,
            AccountWorkDayModule,
            ContractModule,
            DriveRoute,
            InvoiceModule,
            TicketModule,
            ServiceModule,
            DocumentModule,
            MarketingModule,
            ReportModule,

            JobModule,
            JobCategoryModule,
            ResumeModule,
            ...DayoffsRoutes,
            OncallAdminModule,
            VCard,
            KPIModule,
            APIUsageTemplateModule,
            RolesModule,
            ReleaseNotesModule,
            AccountModule,
            SettingsRoute,
            TendersRoute,
            SurveyRoute,
            MailerCampaignModule,
            // The package does not import the app's ErrorPage; the host adds it (#59).
            { ...WarehouseModule, errorElement: <ErrorPage status={500} /> },
            LivechatRoute,
            ResellerRoute,
            AffiliateModule,
            PromoModule,
            TechnicalModule,
            WatchpointModule,
            ThresholdModule,
            TechnicalReportModule,
            AssetModule,
            ToolsModule,
            PurchasingPaymentsModule,
            OperationsModule,
        ],
    },
    {
        path: '/auth',
        element: <AuthLayout />,
        children: [AuthModule],
    },
    {
        path: '/util',
        children: [EmployeePublicModule, TicketPublicModule],
    },
    // Public pages (no auth required)
    TicketFeedbackPublic,
    VCardPublic,
])

const App = () => {
    useFcmToken()
    useDefinedSentryUser()

    // Catches lazy pages outside the main shell (auth, public pages).
    return (
        <Suspense fallback={<PageFallback />}>
            <RouterProvider router={router} />
        </Suspense>
    )
}

export default App
